/**
 * Browser cache of prototype snapshot ZIPs (PROTOTYPER_STABILITY_TECH_PLAN.md P6.4d-2), so
 * switching back to a version skips the download. It lives in OPFS and every tab of the origin
 * shares it:
 * - Keyed by the snapshot row, its revision and the stored object's ETag. A replaced snapshot
 *   gets a new key, so a cached file is never stale.
 * - One writer per key: a Web Lock, and a tab that finds it taken leaves the write to the tab
 *   holding it. `createWritable` replaces the file only when the write closes, so a reader
 *   never sees half a ZIP.
 * - Reads copy the file into memory, so another tab evicting it later cannot break a restore.
 * - Bounded by size and count; the oldest saved files go first, under one eviction lock.
 *
 * Every failure degrades to "not cached": the caller downloads as before.
 */

import { hashFileContent } from './workbenchFileHash';

const OPFS_DIR = 'wc-snapshot-zips';
export const SNAPSHOT_ZIP_CACHE_MAX_BYTES = 150 * 1024 * 1024;
export const SNAPSHOT_ZIP_CACHE_MAX_ENTRIES = 40;
const WRITE_LOCK_PREFIX = 'ideoz:snapshot-zip:';
const EVICT_LOCK = 'ideoz:snapshot-zip-cache:evict';

export interface SnapshotZipCacheMeta {
  snapshotId: string;
  revision?: number | null;
  etag?: string | null;
  sizeBytes?: string | null;
}

export function snapshotZipCacheKey(meta: SnapshotZipCacheMeta): string {
  const id = meta.snapshotId.replace(/[^A-Za-z0-9-]/g, '_');
  const object = hashFileContent(`${meta.etag ?? ''}|${meta.sizeBytes ?? ''}`);
  return `${id}-r${meta.revision ?? 0}-${object}.zip`;
}

export interface CachedZipInfo {
  name: string;
  size: number;
  lastModified: number;
}

/** Oldest first until the cache is within both limits; never the file just written. */
export function selectZipEvictions(
  entries: ReadonlyArray<CachedZipInfo>,
  keep: string,
  limits: { maxBytes: number; maxEntries: number } = {
    maxBytes: SNAPSHOT_ZIP_CACHE_MAX_BYTES,
    maxEntries: SNAPSHOT_ZIP_CACHE_MAX_ENTRIES,
  }
): string[] {
  let total = entries.reduce((sum, entry) => sum + entry.size, 0);
  let count = entries.length;
  const evict: string[] = [];
  for (const entry of [...entries].sort((a, b) => a.lastModified - b.lastModified)) {
    if (total <= limits.maxBytes && count <= limits.maxEntries) break;
    if (entry.name === keep) continue;
    evict.push(entry.name);
    total -= entry.size;
    count -= 1;
  }
  return evict;
}

function supportsOpfs(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'storage' in navigator &&
    'getDirectory' in navigator.storage
  );
}

async function cacheDir(create: boolean): Promise<FileSystemDirectoryHandle | null> {
  if (!supportsOpfs()) return null;
  try {
    const root = await navigator.storage.getDirectory();
    return await root.getDirectoryHandle(OPFS_DIR, { create });
  } catch {
    return null;
  }
}

type LockRequester = (
  name: string,
  options: { ifAvailable: boolean },
  callback: (lock: unknown) => Promise<void>
) => Promise<void>;

/** Runs `work` only when this tab gets the lock; without Web Locks it just runs. */
async function withLockIfAvailable(name: string, work: () => Promise<void>): Promise<void> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) {
    await work();
    return;
  }
  await (locks.request.bind(locks) as unknown as LockRequester)(
    name,
    { ifAvailable: true },
    async (lock) => {
      if (lock) await work();
    }
  );
}

const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04];

export async function readCachedSnapshotZip(key: string): Promise<Blob | null> {
  const dir = await cacheDir(false);
  if (!dir) return null;
  try {
    const file = await (await dir.getFileHandle(key)).getFile();
    if (file.size === 0) return null;
    const bytes = await file.arrayBuffer();
    const head = new Uint8Array(bytes, 0, Math.min(4, bytes.byteLength));
    if (!ZIP_MAGIC.every((byte, i) => head[i] === byte)) {
      await dir.removeEntry(key).catch(() => undefined);
      return null;
    }
    return new Blob([bytes], { type: 'application/zip' });
  } catch {
    return null;
  }
}

async function evictOldEntries(dir: FileSystemDirectoryHandle, keep: string): Promise<void> {
  await withLockIfAvailable(EVICT_LOCK, async () => {
    const infos: CachedZipInfo[] = [];
    const iterable = dir as unknown as {
      entries(): AsyncIterable<[string, FileSystemHandle]>;
    };
    for await (const [name, handle] of iterable.entries()) {
      if (handle.kind !== 'file') continue;
      const file = await (handle as FileSystemFileHandle).getFile();
      infos.push({ name, size: file.size, lastModified: file.lastModified });
    }
    for (const name of selectZipEvictions(infos, keep)) {
      await dir.removeEntry(name).catch(() => undefined);
    }
  });
}

/** Best effort; never throws. */
export async function writeCachedSnapshotZip(key: string, blob: Blob): Promise<void> {
  if (blob.size === 0 || blob.size > SNAPSHOT_ZIP_CACHE_MAX_BYTES) return;
  try {
    await withLockIfAvailable(WRITE_LOCK_PREFIX + key, async () => {
      const dir = await cacheDir(true);
      if (!dir) return;
      const handle = await dir.getFileHandle(key, { create: true });
      const writable = await handle.createWritable();
      try {
        await writable.write(blob);
        await writable.close();
      } catch (err) {
        await writable.abort().catch(() => undefined);
        throw err;
      }
      await evictOldEntries(dir, key);
    });
  } catch {
    // Quota, blocked storage or contention: the version downloads again next time.
  }
}
